import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { BookCallButton, AssessmentButton, GhostLink } from "@/components/Ctas";
import { CALENDLY_URL, ASSESSMENT_URL } from "@/config";

const AREAS = [
  {
    number: "01",
    title: "Strategy",
    tagline: "Find Direction",
    explanation: "When priorities are unclear, strategy isn't translating into action.",
    services: ["Strategic planning", "Product & service strategy", "Prioritization", "Roadmaps", "Opportunity assessment", "Strategy Sprint"],
    testid: "services-area-strategy",
  },
  {
    number: "02",
    title: "Programs & Products",
    tagline: "Make the Work Work",
    explanation: "Turn ideas, programs and products into structured, effective execution.",
    services: ["Program design", "Program management", "Product management", "Execution systems", "Product/service optimization", "Fractional Product Management"],
    testid: "services-area-product-program",
  },
  {
    number: "03",
    title: "Systems & Capacity",
    tagline: "Build Capacity",
    explanation: "Build better ways of working so the organization can operate more effectively and depend less on manual work or individual people.",
    services: ["Process improvement", "Operating systems", "Workflow design", "Delegation", "Documentation", "Ways of working", "Continuous improvement"],
    testid: "services-area-systems-capacity",
  },
  {
    number: "04",
    title: "AI & Automation",
    tagline: "Create Leverage",
    explanation: "Use AI and automation where they create real leverage — not simply because the technology exists.",
    services: ["AI readiness", "AI strategy", "AI opportunity mapping", "Workflow automation", "AI-powered workflows", "Practical AI implementation"],
    testid: "services-area-ai-automation",
  },
];

const ENGAGEMENTS = [
  {
    number: "01",
    title: "Focused Strategy Work",
    description:
      "For a defined strategic challenge, decision, opportunity, or planning need.",
    fit: "I have a specific challenge and need clarity and direction.",
    cta: "Explore Strategy Sprint",
    to: "/workshops#strategy-sprint",
    external: false,
    areas: ["Strategy", "Product / service strategy", "Prioritization", "AI strategy"],
    testid: "services-engagement-strategy-work",
  },
  {
    number: "02",
    title: "Workshops & Facilitation",
    description:
      "For teams that need alignment, capability building, structured decision-making, or facilitated working sessions.",
    fit: "My team needs alignment, learning, or structured working sessions.",
    cta: "Explore Workshops",
    to: "/workshops#the-workshops",
    external: false,
    areas: ["Strategy", "Capability building", "Systems & capacity", "AI & automation"],
    testid: "services-engagement-workshops",
  },
  {
    number: "03",
    title: "Ongoing / Fractional Support",
    description:
      "For organizations that need ongoing product, program, strategy, systems, or AI support without hiring a full-time senior resource.",
    fit: "We need experienced strategic, product or operational support over time.",
    cta: "Talk About Your Needs",
    to: CALENDLY_URL,
    external: true,
    areas: [
      "Product / service strategy",
      "Product or program management",
      "Systems and process improvement",
      "AI strategy and implementation",
    ],
    testid: "services-engagement-fractional",
  },
];

const AUDIENCES = [
  {
    tag: "FOUNDERS",
    title: "Build the business behind the idea.",
    cta: "Explore Founder Support",
    to: "/founders",
    testid: "services-audience-founders",
  },
  {
    tag: "SERVICE BUSINESSES",
    title: "Build a more scalable way of working.",
    cta: "Explore Business Support",
    to: "/businesses",
    testid: "services-audience-businesses",
  },
  {
    tag: "NONPROFITS",
    title: "Strengthen the organization behind the mission.",
    cta: "Explore Nonprofit Support",
    to: "/nonprofits",
    testid: "services-audience-nonprofits",
  },
];

export default function Services() {
  useEffect(() => {
    document.title = "Services — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="services-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              Services
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              Practical strategy. Stronger systems. Better ways of working.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              KP Strategy helps organizations turn goals into action — through
              strategy, programs and products, stronger systems, and practical
              AI.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <BookCallButton testid="services-cta-book-call" />
              <GhostLink
                testid="services-cta-assessment"
                href={ASSESSMENT_URL}
                external
              >
                Get Your Free Assessment
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Four service areas */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="services-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — Service Areas"
            title="Four practices. One way of working."
            testid="services-areas-head"
          />
          <div className="mt-14 border-t border-ink/10">
            {AREAS.map((a, i) => (
              <Reveal key={a.number} delay={i * 0.06}>
                <div
                  data-testid={a.testid}
                  className="grid grid-cols-1 gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
                >
                  <div className="lg:col-span-5">
                    <p className="font-serif text-5xl font-light text-amber/90">
                      {a.number}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-amber">
                      {a.tagline}
                    </p>
                    <p className="mt-3 max-w-sm text-base leading-relaxed text-body">
                      {a.explanation}
                    </p>
                  </div>
                  <ul className="flex flex-wrap content-start gap-2 lg:col-span-7 lg:pt-2">
                    {a.services.map((s) => (
                      <li
                        key={s}
                        className="border border-line bg-paper px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-body transition-colors duration-300 hover:border-amber/60 hover:text-ink"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement models */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="services-engagement-models">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — Engagement Models"
            title="Three ways to work together."
            copy="Choose the path that fits where you are today — every engagement is shaped around what you actually need, not a fixed package."
            testid="services-engagement-models-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            {ENGAGEMENTS.map((e, i) => {
              const linkProps = e.external
                ? { href: e.to, target: "_blank", rel: "noopener noreferrer" }
                : {};
              const CtaWrapper = e.external ? "a" : Link;
              const wrapperProps = e.external ? linkProps : { to: e.to };
              return (
                <Reveal key={e.number} delay={i * 0.08} className="h-full">
                  <div
                    data-testid={e.testid}
                    className="group flex h-full flex-col border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                  >
                    <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-amber">
                      {e.number}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl font-normal text-ink">
                      {e.title}
                    </h3>
                    <span className="mt-5 block h-px w-10 bg-amber transition-all duration-500 group-hover:w-full" />
                    <p className="mt-5 text-base leading-relaxed text-body">
                      {e.description}
                    </p>
                    <p className="mt-4 font-serif text-base italic leading-relaxed text-ink/70">
                      &ldquo;{e.fit}&rdquo;
                    </p>
                    {e.areas && (
                      <ul className="mt-5 flex flex-wrap gap-2">
                        {e.areas.map((a) => (
                          <li
                            key={a}
                            className="border border-line bg-paper px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-body"
                          >
                            {a}
                          </li>
                        ))}
                      </ul>
                    )}
                    <CtaWrapper
                      data-testid={`${e.testid}-cta`}
                      {...wrapperProps}
                      className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-ink transition-colors duration-300 hover:text-amber"
                    >
                      {e.cta}
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </CtaWrapper>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who these services are for */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="services-audiences">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="03 — Who It's For"
            title="Shaped around the organization you run."
            testid="services-audiences-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.tag} delay={i * 0.08} className="h-full">
                <Link
                  data-testid={a.testid}
                  to={a.to}
                  className="group flex h-full flex-col border border-line bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">
                    {a.tag}
                  </p>
                  <h3 className="mt-5 font-serif text-xl font-normal leading-snug text-ink sm:text-2xl">
                    {a.title}
                  </h3>
                  <span className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-ink transition-colors duration-300 group-hover:text-amber">
                    {a.cta}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="services-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              04 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Get Your Free Strategy &amp; Systems Assessment
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Identify where you&apos;re losing clarity, capacity or momentum —
              and what to focus on next.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="services-final-cta-assessment" dark />
              <GhostLink
                testid="services-final-cta-book-call"
                href={CALENDLY_URL}
                dark
              >
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
